import classNames from 'classnames';
import type React from 'react';
import { type CSSProperties, type ReactElement, useEffect } from 'react';
import type { GroupBase } from 'react-select';

import { InfoTipIcon, RevelioTooltip, Select, Text } from '@revelio/ui';

import { useToast } from '../../../shared/hooks/useToast';
import type { SelectedOptionsInterface } from '../data-enrichment/types/dataEnrichmentTypes';
import { findUniqueBestMatches } from './find-unique-best-matches';
import styles from './mapping-step.module.css';

const ASSIGN_COLUMNS_TOOLTIP =
  'Assign your uploaded columns to our column naming system.';

type MappingStepStoreState = {
  mappedColumns?: { [arg0: string]: string }[] | null;
  headers?: string[];
  combinedData?: object[];
};

type UseUploadStore<State> = <T>(selector: (state: State) => T) => T;

interface MappingStepProps<State extends MappingStepStoreState> {
  onSubmit: () => void;
  columnOptions: SelectedOptionsInterface[];
  selectedOptions: SelectedOptionsInterface[];
  setSelectedOptions: React.Dispatch<
    React.SetStateAction<SelectedOptionsInterface[]>
  >;
  ContainerElement: ({
    onSubmit,
    children,
  }: {
    onSubmit: () => void;
    children: ReactElement;
  }) => ReactElement;
  maxSampleDataColumnWidth?: string;
  useUploadStore: UseUploadStore<State>;
}
function MappingStep<State extends MappingStepStoreState>({
  columnOptions: options,
  onSubmit,
  selectedOptions,
  setSelectedOptions,
  ContainerElement,
  maxSampleDataColumnWidth = '1fr',
  useUploadStore,
}: MappingStepProps<State>) {
  const { mappedColumns, headers, combinedData } = useUploadStore((state) => ({
    mappedColumns: state.mappedColumns ?? [],
    headers: state.headers ?? [],
    combinedData: state.combinedData ?? [],
  }));
  const toast = useToast();
  useEffect(() => {
    const hasNoColumnMappings = selectedOptions?.length === 0;
    const hasPreviousColumnMapping = mappedColumns?.length;
    if (hasPreviousColumnMapping && hasNoColumnMappings) {
      const previouslyMappedColumns = Array.from(headers, (item) => {
        const mappedFileHeaderItem = mappedColumns?.find(
          (mapping) => mapping[item]
        );
        // [item as keyof typeof data.mappedColumns];
        if (!mappedFileHeaderItem) {
          return null;
        }

        const mappedHeaderOption = options.find(
          (x) => x?.value === mappedFileHeaderItem[item]
        ) as SelectedOptionsInterface;
        return mappedHeaderOption;
      });
      setSelectedOptions(previouslyMappedColumns);
      return;
    }

    if (hasNoColumnMappings) {
      const defaultSelectedOptions = findUniqueBestMatches(
        headers || [],
        options
          ? options.map((opt) => ({
              label: opt?.value as string,
              value: opt?.value as string,
            }))
          : []
      );
      setSelectedOptions(defaultSelectedOptions);
    }
  }, [headers, options, setSelectedOptions, selectedOptions, mappedColumns]);

  const getGroupedOptions = (index: number) => {
    const usedOptions = selectedOptions
      .filter(
        (option: SelectedOptionsInterface, idx: number) =>
          option && idx !== index
      )
      .map((option: SelectedOptionsInterface) => ({ ...option }));

    const availableOptions = options
      .filter(
        (opt) =>
          opt?.label &&
          opt?.value &&
          !selectedOptions.some(
            (selectedOpt: SelectedOptionsInterface) =>
              selectedOpt && selectedOpt?.value === opt?.value
          )
      )
      .map((opt) => ({ label: opt?.label || '', value: opt?.value || '' })); // Provide fallbacks

    const groupedOptions: {
      label: string;
      options: Readonly<SelectedOptionsInterface[]>;
    }[] = [];

    if (availableOptions.length) {
      groupedOptions.push({
        label: 'Unassigned Columns',
        options: availableOptions as Readonly<SelectedOptionsInterface[]>,
      });
    }

    if (usedOptions.length) {
      groupedOptions.push({
        label: 'Assigned Columns',
        options: usedOptions as Readonly<SelectedOptionsInterface[]>,
      });
    }

    return groupedOptions;
  };

  const handleSelectOption = (
    selectedOption: SelectedOptionsInterface | null,
    index: number
  ) => {
    setSelectedOptions(
      (prevSelectedOptions: SelectedOptionsInterface[] | null) => {
        const updatedSelectedOptions = prevSelectedOptions
          ? [...prevSelectedOptions]
          : [];

        const existingIndex = updatedSelectedOptions.findIndex(
          (opt) => opt && opt?.value === selectedOption?.value
        );
        if (existingIndex > -1) {
          updatedSelectedOptions[existingIndex] = null;
        }
        updatedSelectedOptions[index] = selectedOption;
        return updatedSelectedOptions;
      }
    );
  };

  const formatGroupLabel = (group: GroupBase<SelectedOptionsInterface>) => (
    <span
      className={
        group.label === 'Assigned Columns'
          ? styles.assignedGroupLabel
          : styles.availableGroupLabel
      }
    >
      {group.label}
    </span>
  );

  const tableColHeadings = [
    'Uploaded Columns',
    'Sample Data',
    ' Assign Columns',
  ];

  interface CombinedDataInterface {
    header: string;
    sampleData: string[];
  }

  return ContainerElement({
    onSubmit: () => {
      if (selectedOptions.filter((option) => option).length >= 1) {
        onSubmit();
        return;
      }

      toast({
        status: 'error',
        variant: 'left-accent',
        position: 'top-right',
        title: 'No mapped columns!',
        description: 'You must map at least one or more columns!',
        isClosable: true,
      });
    },
    children: (
      <div className={styles.mappingTable}>
        <div className={styles.headerRow}>
          {tableColHeadings.map((heading: string, index: number) => {
            if (index < 2) {
              return (
                <Text
                  key={`${heading}-${index}`}
                  fontSize="inherit"
                  className={classNames(
                    styles.columnHeading,
                    styles.paddedCell
                  )}
                >
                  {heading}
                </Text>
              );
            } else {
              return (
                <div className={styles.headingCell} key={`${heading}-${index}`}>
                  <Text fontSize="inherit" className={styles.columnHeading}>
                    {heading}
                  </Text>
                  <RevelioTooltip
                    label={ASSIGN_COLUMNS_TOOLTIP}
                    textAlign="center"
                    placement="top"
                  >
                    <InfoTipIcon size={12} />
                  </RevelioTooltip>
                </div>
              );
            }
          })}
        </div>
        <div className={styles.rowList}>
          {combinedData?.map((item, index: number) => {
            const { header, sampleData } = item as CombinedDataInterface;
            return (
              <div
                key={`${header}-${index}`}
                className={styles.row}
                data-assigned={selectedOptions[index] !== null}
                style={
                  {
                    '--sample-column-width': maxSampleDataColumnWidth,
                  } as CSSProperties
                }
              >
                <Text fontSize="inherit" className={styles.paddedCell}>
                  {header}
                </Text>
                <Text
                  fontSize="inherit"
                  className={classNames(styles.paddedCell, styles.sampleCell)}
                >
                  {sampleData.map((value: string, index: number) => (
                    <span key={`${value}-${index}`} title={value}>
                      {value}
                      {index !== sampleData.length - 1 && <br />}
                    </span>
                  ))}
                </Text>
                <div className={styles.selectCell}>
                  <Select<SelectedOptionsInterface>
                    key={index}
                    value={selectedOptions[index]}
                    options={getGroupedOptions(index)}
                    onChange={(option) => handleSelectOption(option, index)}
                    placeholder={`Assign this column ${
                      combinedData.length > 1 ? '(optional)' : ''
                    }`}
                    isClearable
                    formatGroupLabel={formatGroupLabel}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ),
  });
}

export default MappingStep;
